import { useState, type JSX } from "react";
import { EntityCardRenderer } from "../../runtime/EntityCardRenderer";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { Icon } from "../ui/Icon";
import { ExpansionContext } from "./expansionContext";

export interface ExpandableEntityCardProps {
  readonly schemaId: string;
  readonly data: Readonly<Record<string, unknown>>;
  readonly runtime: AppRuntime;
  /** Forwarded to the registered card; see `EntityRendererProps.action`. */
  readonly action?: string;
}

/**
 * Wraps an entity card with an optional in-place expansion.
 *
 * Per `docs/frontend/module-standard.md` ("ONE COMPONENT PER ENTITY")
 * the module registers a SINGLE entity card (`EntityCard`) which is
 * the only renderer for the schema. This wrapper:
 *
 *   1. Renders the registered card once.
 *   2. Reads the chevron-gating helper `reg.hasMore(data)`; if true,
 *      shows a chevron and pipes `expanded` through `ExpansionContext`
 *      so the same card can swap its compact vs full layout on the
 *      fly. The component itself is NOT remounted — it just re-reads
 *      the context and re-renders.
 *   3. If `hasMore` returns false, no chevron, card always renders
 *      in its compact (`expanded === false`) form.
 *
 * Forbidden: `reg.ExpandedRender`. The dual-card pattern has been
 * removed from the codebase; module registrations no longer carry
 * an `ExpandedEntityCard`.
 */
export function ExpandableEntityCard({
  schemaId,
  data,
  runtime,
  action,
}: ExpandableEntityCardProps): JSX.Element {
  const reg = runtime.agent.resolveEntityRenderer(schemaId);
  const [open, setOpen] = useState(false);

  const canExpand = reg?.hasMore?.(data, runtime) ?? false;

  if (!canExpand) {
    return <EntityCardRenderer schemaId={schemaId} data={data} runtime={runtime} action={action} />;
  }

  return (
    <ExpansionContext.Provider value={{ bare: true, expanded: open }}>
      <div className="rounded-lg border border-edge bg-surface-tertiary">
        <div className="flex items-start">
          <div className="flex-1 min-w-0">
            <EntityCardRenderer schemaId={schemaId} data={data} runtime={runtime} action={action} />
          </div>
          <button
            type="button"
            data-testid="expand-chevron"
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen((v) => !v);
            }}
            className="mr-2 mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded text-content-tertiary hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
          >
            <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
          </button>
        </div>
      </div>
    </ExpansionContext.Provider>
  );
}
